import {
    Box,
    Button,
    Container,
    Grid2,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import { AuthStatus, useAuth } from '../auth/Auth';
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingPage from '../loading/LoadingPage';
import { ListQuestionsResponse } from '../api/theSourceApi';
import { Question } from '../database/theSource';
import QuestionSubmissionForm from './QuestionSubmissionForm';
import { TabContext, TabPanel } from '@mui/lab';
import SocialBar from '../socials/SocialBar';
import TheSourceTab, { TabValue } from './TheSourceTab';
import { TheSourceFilters, useFilters } from './TheSourceFilters';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { QuestionStatus } from './constants/QuestionStatus';
import { ResponseType } from './constants/ResponseType';
import { PrivacyStatus } from './constants/PrivacyStatus';
import { useRequest } from '../api/Request';
import { User, UserSummary } from '../database/user';
import { useApi } from '../api/Api';
import { useCache } from '../api/cache/Cache';

const TheSourcePage = () => {
    const auth = useAuth();
    const user = auth.user;
    const [data, setData] = useState<ListQuestionsResponse>();
    const [value, setValue] = useState<TabValue>('recent');
    const [showFilters, setShowFilters] = useState(true);
    const isMobile = useMediaQuery('(max-width:800px)');
    const api = useApi();
    const [artist, setArtist] = useState<User>();
    const getArtistUserRequest = useRequest<User>();
    const cache = useCache();

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    const onCreateQuestion = (question: Question) => {
        setValue('my');
        const newData = data?.questions || [];
        setData({
            questions: [question, ...newData],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const filters = useFilters();

    const filteredQuestions = useMemo(() => {
        if (!data?.questions) return [];

        return data.questions.filter((question) => {
            const matchesResponseType = filters.responseTypeFilters.includes(
                question.responseType as ResponseType,
            );
            const matchesStatus = filters.statusFilters.includes(
                question.status as QuestionStatus,
            );
            const matchesPrivacyStatus = filters.privacyStatusFilters.includes(
                question.isPrivate === 'true'
                    ? PrivacyStatus.Private
                    : PrivacyStatus.Public,
            );
            return matchesResponseType && matchesStatus && matchesPrivacyStatus;
        });
    }, [data, filters]);

    useEffect(() => {
        if (!getArtistUserRequest.isSent()) {
            getArtistUserRequest.onStart();
            api.getUserPublic('artistUsername')
                .then((resp) => {
                    setArtist(resp.data);
                    const artistUserSummary: UserSummary = {
                        username: resp.data.username,
                        displayName: resp.data.displayName,
                        isArtist: resp.data.isArtist,
                        subscriptionStatus: resp.data.subscriptionStatus,
                        subscriptionOverride: resp.data.subscriptionOverride,
                        quote: resp.data.quote,
                    };
                    cache.userSummaries.put(artistUserSummary);
                    cache.userSummaries.markFetched(resp.data.username);
                    getArtistUserRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('Error fetching artist data:', err);
                    getArtistUserRequest.onFailure(err);
                });
        }
    }, [getArtistUserRequest, api, cache.userSummaries]);

    if (auth.status === AuthStatus.Loading) {
        return <LoadingPage />;
    }

    return (
        <Container maxWidth={false} sx={{ pt: 6, mb: isMobile ? '100px' : 0 }}>
            <Stack spacing={4}>
                <QuestionSubmissionForm
                    artist={artist}
                    onSubmit={(e) => onCreateQuestion(e)}
                />
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 2.5, xl: 2 }}>
                        <Button
                            onClick={toggleFilters}
                            startIcon={showFilters ? <VisibilityOff /> : <Visibility />}
                            variant='outlined'
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(139, 0, 0, 0.08)',
                                },
                            }}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                        <AnimatePresence>
                            {showFilters && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <TheSourceFilters filters={filters} tabValue={value} />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Grid2>
                    <Grid2
                        size={{
                            xs: 12,
                            md: showFilters ? 9.5 : 12,
                            xl: showFilters ? 10 : 12,
                        }}
                    >
                        {artist && (
                            <TabContext value={value}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        typography: 'body1',
                                        justifyContent: 'center',
                                        display: 'flex',
                                    }}
                                >
                                    <Tabs
                                        value={value}
                                        onChange={(_, t) => setValue(t)}
                                        textColor='inherit'
                                        variant='scrollable'
                                    >
                                        {user && user.isArtist && (
                                            <Tab
                                                label={QuestionStatus.Pending}
                                                value={QuestionStatus.Pending}
                                            />
                                        )}
                                        <Tab label='Recent' value='recent' />
                                        {!user ? (
                                            <Tooltip title='Sign in or up to view your requests'>
                                                <span>
                                                    <Tab
                                                        label='My Requests'
                                                        value='my'
                                                        disabled
                                                        icon={<Lock />}
                                                        iconPosition='start'
                                                    />
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Tab label='My Requests' value='my' />
                                        )}
                                        {/* <Tab label='My Requests' value='my' disabled={!user} icon={!user ? <Lock /> : undefined} iconPosition={!user ? "start" : undefined} /> */}
                                    </Tabs>
                                </Box>
                                <TabPanel
                                    value={QuestionStatus.Pending}
                                    sx={{ px: { xs: 0, sm: 3 } }}
                                >
                                    <TheSourceTab
                                        artist={artist}
                                        query={value}
                                        data={{
                                            questions: filteredQuestions,
                                            lastEvaluatedKey: data?.lastEvaluatedKey,
                                        }}
                                        setData={setData}
                                    />
                                </TabPanel>
                                <TabPanel value='recent' sx={{ px: { xs: 0, sm: 3 } }}>
                                    <TheSourceTab
                                        artist={artist}
                                        query={value}
                                        data={{
                                            questions: filteredQuestions,
                                            lastEvaluatedKey: data?.lastEvaluatedKey,
                                        }}
                                        setData={setData}
                                    />
                                </TabPanel>
                                <TabPanel value='my' sx={{ px: { xs: 0, sm: 3 } }}>
                                    <TheSourceTab
                                        artist={artist}
                                        query={value}
                                        data={{
                                            questions: filteredQuestions,
                                            lastEvaluatedKey: data?.lastEvaluatedKey,
                                        }}
                                        setData={setData}
                                    />
                                </TabPanel>
                            </TabContext>
                        )}
                    </Grid2>
                </Grid2>
            </Stack>
            <SocialBar />
        </Container>
    );
};

export default TheSourcePage;
